import React from 'react';
import { makeStyles } from '@material-ui/core';
import ReactHtmlParser from 'react-html-parser';

import Logo from '@root/assets/logo/white.svg';
import adIllustration from '@root/assets/copysmith-workflow-large.png';
import { escapeText } from '@root/utils/text.utils';
import useTextSelect from '@root/utils/hooks/useTextSelect';

import EarthIcon from './earthIcon.png';
import LikeLoveWow from './likeLoveWow.png';

const useStyles = makeStyles((theme) => ({
  logo: {
    width: '40px',
    borderRadius: theme.border.radius.default,
  },
  companyInfo: {
    position: 'relative',
    left: '3rem',
    bottom: '2.25rem',
    fontFamily: 'Helvetica, Inter, sans-serif',
    lineHeight: '1.195rem',
    width: 'calc(100% - 3rem)',
  },
  companyNameWrap: {
    color: 'rgb(8, 8, 8)',
    fontSize: '0.9125rem',
  },
  sponsoredWrap: {
    color: 'rgb(101, 103, 107)',
    fontSize: '0.85rem',
  },
  earthIcon: {
    width: '12px',
    marginTop: '-2px',
  },
  mainImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'calc(100% + 32px)',
    marginTop: '10px',
    marginLeft: '-16px',
    height: '260px',
    borderTop: '1px solid rgb(220, 220, 220)',
    borderBottom: '1px solid rgb(220, 220, 220)',
  },
  callToAction: {
    position: 'relative',
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    borderBottom: '1px solid rgb(220, 220, 220)',
    backgroundColor: 'rgb(240, 242, 245)',
    fontFamily: 'Helvetica, Inter, sans-serif',
    fontSize: '1rem',
    color: 'rgb(101, 103, 107)',
  },
  adDomainWrap: {
    fontSize: '0.7625rem',
  },
  tryNowButton: {
    position: 'absolute',
    color: 'rgb(5, 5, 5)',
    fontSize: '0.9rem',
    fontWeight: 600,
    textAlign: 'center',
    paddingTop: '0.5rem',
    top: 20,
    right: 18,
    width: '100px',
    height: '38px',
    borderRadius: theme.border.radius.default,
    background: 'rgb(228, 230, 235)',
  },
  bottomBlock: {
    padding: theme.spacing(1, 2),
  },
}));

/**
 * Returns a random integer between min (inclusive) and max (inclusive).
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 * Using Math.round() will give you a non-uniform distribution!
 */
function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getLikes() {
  return getRandomInt(25, 90);
}

const getSelectedContentField = ({
  selection,
  headlineRef,
  linkDescriptionRef,
  descriptionRef,
}) => {
  const { anchorNode, focusNode } = selection;

  // check start selection node
  if (headlineRef.current.contains(anchorNode)) {
    return 0;
  }
  if (linkDescriptionRef.current.contains(anchorNode)) {
    return 1;
  }
  if (descriptionRef.current.contains(anchorNode)) {
    return 2;
  }

  // check end selection node
  if (headlineRef.current.contains(focusNode)) {
    return 0;
  }
  if (linkDescriptionRef.current.contains(focusNode)) {
    return 1;
  }
  if (descriptionRef.current.contains(focusNode)) {
    return 2;
  }

  return null;
};

const FacebookAdPreview = ({
  content: initialContent,
  fileData: {
    companyName,
  },
  onTextSelect = () => null,
}) => {
  const classes = useStyles();
  let content = initialContent;
  if (!Array.isArray(content)) {
    content = [initialContent, '', ''];
  }
  const { 0: headline, 1: linkDescription, 2: mainContent } = content;
  const adDomain = `${(companyName || '').toUpperCase().replace(' ', '')}.COM`;
  const [likesCount] = React.useState(getLikes());

  const headlineRef = React.useRef();
  const linkDescriptionRef = React.useRef();
  const descriptionRef = React.useRef();

  useTextSelect((selection) => {
    const field = getSelectedContentField({
      selection,
      headlineRef,
      linkDescriptionRef,
      descriptionRef,
    });

    if (field === null) {
      return;
    }

    const text = escapeText(selection.toString());

    if (!text || !escapeText(content[field]).includes(text)) {
      onTextSelect(null);
      return;
    }

    onTextSelect({
      text,
      field,
    });
  });

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <div className="companyInfoContainer">
          <img
            src={Logo}
            alt="Copysmith Logo"
            className={classes.logo}
          />
          <div className={classes.companyInfo}>
            <div className={classes.companyNameWrap}>
              <b>{companyName}</b>
            </div>
            <div className={classes.sponsoredWrap}>
              Sponsored ·
              {' '}
              <img
                src={EarthIcon}
                alt="Earth Icon"
                className={classes.earthIcon}
              />
            </div>
          </div>

          <div style={{ marginTop: '-0.825rem', color: 'rgb(5, 5, 5)' }}>
            <div
              style={{
                fontSize: '0.925rem',
              }}
              ref={descriptionRef}
            >
              <span>
                {mainContent}
              </span>
            </div>
          </div>
        </div>
        <div className={classes.mainImageContainer}>
          <img
            src={adIllustration}
            alt="Paper airplane illustration"
            style={{ width: '260px' }}
          />
        </div>
        <div className={classes.callToAction}>
          <div className={classes.bottomBlock}>
            <div className={classes.adDomainWrap}>
              {adDomain}
            </div>
            <div
              style={{
                color: 'rgb(5, 5, 5)',
                fontWeight: 600,
                width: '80%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              ref={headlineRef}
            >
              <span>
                {ReactHtmlParser(headline)}
              </span>
            </div>
            <div style={{ fontSize: '0.95rem' }} ref={linkDescriptionRef}>
              <span>
                {ReactHtmlParser(linkDescription)}
              </span>
            </div>
            <div className={classes.tryNowButton}>
              Try Now
            </div>
          </div>
        </div>
        <div
          className="emoticonContainer"
          style={{ padding: 0, marginTop: 12 }}
        >
          <img
            src={LikeLoveWow}
            alt="Like, Love, Wow Emoticons"
            style={{
              marginTop: 12,
              marginBottom: 24,
              height: 31,
              padding: 0,
              margin: 0,
            }}
          />
          <span
            style={{
              fontSize: '0.975rem',
              color: 'grey',
            }}
          >
            {likesCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FacebookAdPreview;
